import { useCallback, useMemo, useState } from 'react';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import {
  buildDefinitionFromStore,
  selectIsEditable,
  useCohortDefinitionStore,
} from '@verantos-dev/feature-analytical-variables';
import { CriteriaSelection } from '@verantos-dev/types-cohorts';

import { useAvLibraryItems } from './useAvLibraryItems';
import { useEmitResearchAssistantUpdate } from '../../hooks/common/useEmitResearchAssistantUpdate';
import {
  getAvIneligibilityReason,
  type AvIneligibilityReason,
} from '../../utils/avLibraryEligibility';

export const ROLE_OPTIONS = [
  { value: CriteriaSelection.INCLUSION, label: 'Inclusion' },
  { value: CriteriaSelection.EXCLUSION, label: 'Exclusion' },
];

export const useAddVariableFromLibraryModal = (onClose: () => void) => {
  const [selectedAvIds, setSelectedAvIds] = useState<Set<string>>(new Set());
  const [role, setRole] = useState<string>(CriteriaSelection.INCLUSION);
  const [addError, setAddError] = useState(false);

  const isGen3 = useFeatureFlagSplit('research_assistant_gen_3');
  // TODO: cleanup with ra_conditional_index_date ff
  const isConditionalIndexDateEnabled = useFeatureFlagSplit('ra_conditional_index_date');

  const emitResearchAssistantUpdate = useEmitResearchAssistantUpdate();
  const addAnalyticalVariableCriterionFromLibrary = useCohortDefinitionStore(
    (s) => s.addAnalyticalVariableCriterionFromLibrary
  );
  const addLibraryReferenceCriterion = useCohortDefinitionStore(
    (s) => s.addLibraryReferenceCriterion
  );
  const isEditable = useCohortDefinitionStore(selectIsEditable);

  const {
    allItems,
    isLoading,
    isError,
    inCohortAvIds,
    nameConflictAvIds,
    dependentCriteriaMode,
    searchQuery,
    handleSearchChange,
    filterBySearch,
  } = useAvLibraryItems();

  // When FF is off, exclude SQL-only AVs (jsonValue === null): the clone path
  // requires a structured definition to copy.
  const eligibleItems = useMemo(
    () =>
      allItems.filter((item) => {
        // OR-6638: a variable declared row_level="event" returns one row per
        // qualifying event, not one value per patient — never eligible as a
        // cohort criterion.
        if (item.rowLevel === 'event') return false;
        if (!isGen3 && item.jsonValue === null) return false;
        // Blocking AV-IR until migration is complete
        if (isGen3 && item.sqlBody === null) return false;
        return item.format.toLowerCase() === 'boolean';
      }),
    [allItems, isGen3]
  );

  const filteredItems = useMemo(
    () => filterBySearch(eligibleItems),
    [eligibleItems, filterBySearch]
  );

  // Rows the cohort's conditional index cannot evaluate; shown disabled with the reason.
  // TODO: cleanup with ra_conditional_index_date ff
  const disabledReasons = useMemo(() => {
    const reasons = new Map<string, AvIneligibilityReason>();
    if (!isConditionalIndexDateEnabled) return reasons;
    for (const item of eligibleItems) {
      const reason = getAvIneligibilityReason(item, dependentCriteriaMode);
      if (reason) reasons.set(item.analyticalVariableId, reason);
    }
    return reasons;
  }, [eligibleItems, dependentCriteriaMode, isConditionalIndexDateEnabled]);

  const handleSelectAv = useCallback((avId: string) => {
    setSelectedAvIds((prev) => {
      const next = new Set(prev);
      if (next.has(avId)) {
        next.delete(avId);
      } else {
        next.add(avId);
      }
      return next;
    });
    setAddError(false);
  }, []);

  // Bulk select/clear from the list's Select all toggle.
  const handleSelectAll = useCallback((avIds: string[]) => {
    setSelectedAvIds(new Set(avIds));
    setAddError(false);
  }, []);

  const handleRoleChange = useCallback((newRole: string) => {
    setRole(newRole);
  }, []);

  const handleAdd = useCallback(async () => {
    if (selectedAvIds.size === 0 || !isEditable) return;
    setAddError(false);
    try {
      for (const avId of Array.from(selectedAvIds)) {
        const item = allItems.find((i) => i.analyticalVariableId === avId);
        if (!item) throw new Error('Library analytical variable not found');
        if (isGen3) {
          // TODO: cleanup with research_assistant_gen_3 ff
          // Reference-by-id: criterion carries sourceLibraryAvId only; runner
          // resolves AV body at execution time.
          addLibraryReferenceCriterion(avId, role);
        } else {
          if (!item.jsonValue) throw new Error('Missing analytical variable definition');
          const newAv = { ...item.jsonValue, id: crypto.randomUUID() };
          addAnalyticalVariableCriterionFromLibrary(newAv, role, avId);
        }
      }
      emitResearchAssistantUpdate(buildDefinitionFromStore());
      onClose();
    } catch {
      setAddError(true);
    }
  }, [
    isGen3,
    selectedAvIds,
    isEditable,
    allItems,
    role,
    addAnalyticalVariableCriterionFromLibrary,
    addLibraryReferenceCriterion,
    emitResearchAssistantUpdate,
    onClose,
  ]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return {
    selectedAvIds,
    role,
    searchQuery,
    addError,
    isLoading,
    isError,
    filteredItems,
    inCohortAvIds,
    nameConflictAvIds,
    disabledReasons,
    roleOptions: ROLE_OPTIONS,
    canAdd: selectedAvIds.size > 0 && isEditable,
    handleSelectAv,
    handleSelectAll,
    handleRoleChange,
    handleSearchChange,
    handleAdd,
    handleClose,
  };
};
